import { useId, useMemo, type ReactNode } from 'react';
import { Card } from "#primitives/data-display";
import { GradientOverlay } from "#primitives/data-display";
import {
  gradientVariants,
  type GradientVariant,
} from "#primitives/data-display";
import { cn } from '#utils';
import { getSafeSubscriptionColor } from "./constants";
import { formatSubscriptionPrice } from "./formatSubscriptionPrice";

type TitleTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';

/** Minimal membership data required by these cards. Accepts any superset (full Membership, MembershipDetails, etc.) */
export interface MembershipCardData {
  id?: string | number;
  name: string;
  description?: string | null;
  color?: string | null;
  price?: string | number | null;
  billingFrequency?: string | null;
}

interface MembershipCardGradientProps {
  membership: MembershipCardData;
  /** Shows the "Active" badge. Ignored in compact variant. */
  isActive?: boolean;
  /** Shows the price section derived from the membership. Defaults to true. Ignored in compact variant. */
  showPrice?: boolean;
  /** HTML element used for the membership name. Defaults to 'h3' */
  titleAs?: TitleTag;
  /**
   * Layout variant.
   * - `default`: full card with description, price, optional active badge (used on MembershipsPage and other consumers).
   * - `compact`: 96px review/checkout card — subtitle on top, plan name on bottom, no description/price/badge.
   * - `profile`: 176px Profile-hub card — badge top-left, title below, no description/price (Profile/MenuPage only).
   * @default "default"
   */
  variant?: "default" | "compact" | "profile";
  /**
   * Subtitle shown above the title in `compact` variant only.
   * Ignored in `default` variant.
   * @default "Membership"
   */
  subtitle?: string;
  /** Replaces the boolean "Active" pill's content. Default variant only (both sizes); wins over `isActive`. Ignored in profile/compact variants. */
  badge?: ReactNode;
  /** Default variant only. `lg` = web holdings card (frame 14546:716809). @default "md" */
  size?: 'md' | 'lg';
  /** `lg` size only: white pill on the title row's right edge (e.g. a lesson pack's "5 Hours"). Independent of `badge`. */
  headerLabel?: ReactNode;
  /** Passed to GradientOverlay in every variant. @default true */
  showDots?: boolean;
  /** Profile variant only: right-aligned content on the badge row (e.g. a lesson pack's "3 Hours left"). Ignored in other variants. */
  trailing?: ReactNode;
  className?: string;
}

export const MembershipCardGradient = ({
  membership,
  isActive = false,
  showPrice = true,
  titleAs = 'h3',
  variant = 'default',
  subtitle = 'Membership',
  badge,
  size = 'md',
  headerLabel,
  showDots = true,
  trailing,
  className,
}: MembershipCardGradientProps) => {
  const safeColor = getSafeSubscriptionColor(membership.color);
  const gradient = gradientVariants[safeColor as GradientVariant];
  const labelledById = useId();
  const TitleElement = titleAs;
  const pill = badge ?? (isActive ? 'Active' : null);

  const priceParts = useMemo(() => {
    const { text: amount, isPaid } = formatSubscriptionPrice(membership.price);

    // A zero-cost or unknown price takes no billing-frequency suffix — nothing recurs.
    if (!isPaid) {
      return { amount, suffix: null, a11yLabel: null };
    }

    switch (membership.billingFrequency) {
      case "monthly":
        return { amount, suffix: "/mo", a11yLabel: "per month" };
      case "quarterly":
        return { amount, suffix: "/qtr", a11yLabel: "per quarter" };
      case "semi-annually":
        return { amount, suffix: "/6mo", a11yLabel: "per six months" };
      case "annually":
        return { amount, suffix: "/yr", a11yLabel: "per year" };
      case "one-time":
      default:
        return { amount, suffix: null, a11yLabel: null };
    }
  }, [membership.price, membership.billingFrequency]);

  if (variant === 'profile') {
    return (
      <Card
        as="div"
        rounded="none"
        aria-labelledby={labelledById}
        className={cn(
          "relative h-24 min-h-24 overflow-hidden rounded-18",
          className
        )}
        data-component="MembershipCardGradient"
        data-variant="profile"
      >
        <GradientOverlay gradient={gradient} to="bottom-right" showDots={showDots} />

        <div className="relative z-10 flex h-full flex-col items-start gap-4 p-4">
          {trailing == null ? (
            isActive && (
              <span className="inline-flex w-14 items-center justify-center rounded-full bg-white py-1.5 text-xs leading-none font-medium text-butter-gray-600 shadow-sm">
                Active
              </span>
            )
          ) : (
            <div className="flex w-full items-center justify-between gap-3">
              {isActive && (
                <span className="inline-flex w-14 items-center justify-center rounded-full bg-white py-1.5 text-xs leading-none font-medium text-butter-gray-600 shadow-sm">
                  Active
                </span>
              )}
              <span className="ml-auto shrink-0 text-sm font-medium text-white">{trailing}</span>
            </div>
          )}
          <TitleElement
            id={labelledById}
            className="text-lg font-semibold leading-tight text-white"
            data-testid={`plan-card-title`}
          >
            {membership.name}
          </TitleElement>
        </div>
      </Card>
    );
  }

  if (variant === 'compact') {
    return (
      <Card
        as="div"
        rounded="none"
        aria-labelledby={labelledById}
        className={cn(
          "relative h-24 min-h-24 overflow-hidden rounded-18",
          className
        )}
        data-component="MembershipCardGradient"
        data-variant="compact"
      >
        <GradientOverlay gradient={gradient} to="bottom-right" showDots={showDots} />

        <div className="relative z-10 flex h-full flex-col justify-between p-4">
          <p className="text-sm font-normal leading-tight text-white/90">
            {subtitle}
          </p>
          <TitleElement
            id={labelledById}
            className="text-lg font-semibold leading-tight text-white"
            data-testid={`plan-card-title`}
          >
            {membership.name}
          </TitleElement>
        </div>
      </Card>
    );
  }

  const priceNode = showPrice ? (
    <p className={`flex items-baseline gap-1${size === 'lg' ? '' : ' mt-2'}`}>
      <span
        className={size === 'lg' ? 'text-28 font-semibold text-white' : 'text-lg font-semibold text-white'}
        data-testid={`plan-card-price`}
      >
        {priceParts.amount}
      </span>
      {priceParts.suffix ? (
        <>
          <span className="text-xs font-medium text-white/70" aria-hidden="true">
            {priceParts.suffix}
          </span>
          <span className="sr-only">{priceParts.a11yLabel}</span>
        </>
      ) : null}
    </p>
  ) : null;

  if (size === 'lg') {
    return (
      <Card
        as="div"
        rounded="none"
        bordered={false}
        shadow={false}
        aria-labelledby={labelledById}
        className={cn(
          "relative overflow-hidden rounded-20 border border-butter-gray-200",
          className
        )}
        data-component="MembershipCardGradient"
        data-size="lg"
      >
        <GradientOverlay gradient={gradient} to="bottom-right" showDots={showDots} />

        <div className={`relative z-10 flex flex-col items-start ${showPrice ? 'gap-6 p-7' : 'gap-8 p-6'}`}>
          {pill != null && (
            <span className="inline-flex h-8 w-fit items-center rounded-full bg-white px-3 text-sm font-medium text-butter-gray-700">
              {pill}
            </span>
          )}
          <div className="flex w-full flex-col gap-3">
            <div className="flex w-full items-center justify-between gap-3">
              <TitleElement
                id={labelledById}
                className="text-2xl font-semibold text-white"
                data-testid={`plan-card-title`}
              >
                {membership.name}
              </TitleElement>
              {headerLabel != null && (
                <span className="inline-flex h-7 shrink-0 items-center rounded-full bg-white px-3 text-sm font-medium text-butter-gray-700">
                  {headerLabel}
                </span>
              )}
            </div>
            {membership.description ? (
              <p className="text-base font-medium leading-5 text-white">
                {membership.description}
              </p>
            ) : null}
          </div>

          {priceNode}
        </div>
      </Card>
    );
  }

  return (
    <Card
      as="div"
      rounded="3xl"
      aria-labelledby={labelledById}
      className={cn(
        "relative min-h-[7.5rem] overflow-hidden",
        className
      )}
      data-component="MembershipCardGradient"
    >
      <GradientOverlay gradient={gradient} to="bottom-right" showDots={showDots} />

      <div className="relative z-10 flex h-full min-h-[7.5rem] flex-col justify-between p-4">
        {/* Top group: badge + name + description */}
        <div className="flex flex-col gap-1.5">
          {pill != null && (
            <span className="w-fit rounded-full bg-white px-2.5 py-1 text-xs font-medium text-butter-gray-800 shadow-sm">
              {pill}
            </span>
          )}
          <TitleElement
            id={labelledById}
            className="text-base font-semibold leading-tight text-white"
            data-testid={`plan-card-title`}
          >
            {membership.name}
          </TitleElement>
          <p className="text-xs text-white/70 line-clamp-2">
            {membership.description}
          </p>
        </div>

        {/* Bottom: price */}
        {priceNode}
      </div>
    </Card>
  );
};
